/**
 * ErrorBoundary Component
 *
 * React error boundary to catch and display errors gracefully
 */

import { Component, type ReactNode, type ErrorInfo } from 'react';
import '@tylertech/forge/icon';
import '@tylertech/forge/button';
import './ErrorBoundary.css';

export interface ErrorBoundaryProps {
  /** Child components to render */
  children: ReactNode;
  /** Optional fallback UI to display on error */
  fallback?: ReactNode;
  /** Optional error handler callback */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary wrapper component
 *
 * Catches React errors in child components and displays a fallback UI
 *
 * @example
 * ```tsx
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      const IconElement = 'forge-icon' as React.ElementType;
      const ButtonElement = 'forge-button' as React.ElementType;

      return (
        <div className="error-boundary">
          <IconElement name="error" className="error-boundary-icon" />
          <h2 className="error-boundary-title">Something went wrong</h2>
          <p className="error-boundary-message">
            {this.state.error?.message || 'An unexpected error occurred'}
          </p>
          <ButtonElement type="raised" onClick={this.handleReset}>
            <span slot="start">Try Again</span>
          </ButtonElement>
        </div>
      );
    }

    return this.props.children;
  }
}
